import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FieldWrap, NativeSelect } from "@/components/Field";
import { ReceiptField, stillUploading } from "@/components/Receipt";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { todayISO } from "@/lib/format";

const blank = (cfg, pt) => ({ date: todayISO(), amount: "", section_id: cfg.expense_sections[0].id, item_id: "", paid_to: "", payment_type: pt, description: "", collection: "", receipt_id: null });
const inputCls = "h-11 bg-white text-slate-900";

export function ExpenseDialog({ open, record, cfg, section, onClose, onSaved }) {
  const { settings, options } = useApp();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF(record ? { ...record } : { ...blank(cfg, settings.payment_types?.[0] || ""), destination: section || "" }); } }, [open, record, cfg, settings, section]);
  if (!f) return null;
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v, ...(k === "section_id" ? { item_id: "" } : {}) }));
  const sec = cfg.expense_sections.find((s) => s.id === f.section_id);

  const save = async (e) => {
    e.preventDefault();
    if (stillUploading()) return;
    if (!f.item_id) { toast.error("Please choose a line item"); return; }
    setBusy(true);
    const body = { ...f, amount: Number(f.amount) };
    try {
      if (record) await api.put(`/expenses/${record.id}`, body); else await api.post("/expenses", body);
      toast.success(record ? "Expense updated" : "Expense added");
      onSaved();
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="expense-dialog">
        <DialogHeader><DialogTitle>{record ? "Edit expense" : "Add expense"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <FieldWrap label="Date" required><Input type="date" required value={f.date} onChange={(e) => set("date")(e.target.value)} className={inputCls} data-testid="expense-date" /></FieldWrap>
            <FieldWrap label="Amount ($)" required><Input type="number" min="0.01" step="0.01" required value={f.amount} onChange={(e) => set("amount")(e.target.value)} className={inputCls} data-testid="expense-amount" /></FieldWrap>
          </div>
          <FieldWrap label="Section" required><NativeSelect value={f.section_id} onChange={set("section_id")} options={cfg.expense_sections.map((s) => ({ value: s.id, label: s.name }))} testid="expense-section" /></FieldWrap>
          <FieldWrap label="Line item" required sub={sec?.items.length ? null : "No line items yet. Add them in Admin Panel → Financial Report."}>
            <NativeSelect value={f.item_id} onChange={set("item_id")} placeholder="Choose..." options={(sec?.items || []).map((i) => ({ value: i.id, label: i.name }))} testid="expense-item" />
          </FieldWrap>
          <div className="grid gap-4 sm:grid-cols-2">
            <FieldWrap label="Paid to"><Input value={f.paid_to} onChange={(e) => set("paid_to")(e.target.value)} className={inputCls} data-testid="expense-paid-to" /></FieldWrap>
            <FieldWrap label="Payment type"><NativeSelect value={f.payment_type} onChange={set("payment_type")} options={settings.payment_types || []} testid="expense-payment-type" /></FieldWrap>
          </div>
          <FieldWrap label="General Council / MWJK"><NativeSelect value={f.destination || ""} onChange={set("destination")} placeholder="Not set" options={options.destinations.filter((d) => d.active || d.name === f.destination).map((d) => d.name)} testid="expense-destination" /></FieldWrap>
          <FieldWrap label="Collection (event or drive)" sub="Optional. Tag this expense to a conference or drive."><NativeSelect value={f.collection || ""} onChange={set("collection")} placeholder="None" options={options.collections.filter((c) => c.active || c.name === f.collection).map((c) => c.name)} testid="expense-collection" /></FieldWrap>
          <FieldWrap label="Description"><Input value={f.description} onChange={(e) => set("description")(e.target.value)} className={inputCls} data-testid="expense-description" /></FieldWrap>
          <FieldWrap label="Receipt (optional)"><ReceiptField value={f.receipt_id} onChange={set("receipt_id")} testid="expense-receipt" /></FieldWrap>
          <Button type="submit" disabled={busy} className="w-full" data-testid="expense-save">{record ? "Save changes" : "Add expense"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
